/**
 * Product Media Gallery Optimizer
 * - Prioritize first image loading (LCP optimization)
 * - Force lazy loading for remaining images
 * - Improve Swiper initialization timing
 * - Mobile-specific optimizations
 */

(function() {
  'use strict';

  const isMobile = window.innerWidth < 750;

  const config = {
    eagerLoadCount: 1,
    thumbEagerCount: isMobile ? 3 : 5,
    rootMargin: '200px',
    mobileMaxWidth: 600,      // Max image width for mobile
    desktopMaxWidth: 1200,    // Max image width for desktop
    debug: false
  };

  function log(...args) {
    if (config.debug) console.log('[MediaOptimizer]', ...args);
  }

  /**
   * Optimize srcset for faster mobile loading
   * Reduces image size requests on mobile devices
   */
  function optimizeSrcset(img) {
    if (!img.srcset || !isMobile) return;

    const maxWidth = config.mobileMaxWidth;
    const srcsetEntries = img.srcset.split(',').map(s => s.trim());
    
    // Filter srcset to only include sizes <= maxWidth for mobile
    const optimizedEntries = srcsetEntries.filter(entry => {
      const widthMatch = entry.match(/(\d+)w$/);
      if (widthMatch) {
        return parseInt(widthMatch[1]) <= maxWidth;
      }
      return true;
    });

    if (optimizedEntries.length > 0) {
      img.srcset = optimizedEntries.join(', ');
      
      // Update sizes attribute for mobile
      img.sizes = `(max-width: ${maxWidth}px) 100vw, ${maxWidth}px`;
      
      log('Optimized srcset for mobile:', img.srcset.substring(0, 80));
    }
  }

  /**
   * Set optimal src for immediate display
   * Uses smaller image for faster initial paint
   */
  function setOptimalSrc(img, isFirst) {
    if (!img.srcset) return;

    const targetWidth = isMobile ? 480 : 800;
    const srcsetEntries = img.srcset.split(',').map(s => s.trim());
    
    // Find closest match to target width
    let bestMatch = null;
    let bestDiff = Infinity;

    srcsetEntries.forEach(entry => {
      const parts = entry.split(' ');
      const url = parts[0];
      const widthMatch = parts[1]?.match(/(\d+)w/);
      
      if (widthMatch) {
        const width = parseInt(widthMatch[1]);
        const diff = Math.abs(width - targetWidth);
        if (diff < bestDiff) {
          bestDiff = diff;
          bestMatch = url;
        }
      }
    });

    if (bestMatch && isFirst) {
      // Replace src with optimal size for first image
      img.src = bestMatch;
      log('Set optimal src:', bestMatch.substring(0, 60));
    }
  }

  /**
   * Optimize image loading strategy
   */
  function optimizeImageLoading() {
    const galleryWrapper = document.querySelector('.main-product__media--slider-wrapper');
    const thumbsWrapper = document.querySelector('.main-product__media--thumbs-wrapper');
    const gridWrapper = document.querySelector('.main-product__media--grid');

    // Optimize main gallery images
    if (galleryWrapper) {
      const slides = galleryWrapper.querySelectorAll('.swiper-slide');
      slides.forEach((slide, index) => {
        const img = slide.querySelector('img');
        if (!img) return;

        if (index < config.eagerLoadCount) {
          // First image: eager + high priority
          img.loading = 'eager';
          img.fetchPriority = 'high';
          img.decoding = 'sync';
          
          // Mobile: use smaller image for faster load
          if (isMobile) {
            optimizeSrcset(img);
            setOptimalSrc(img, true);
          }
          
          log('Eager load:', index);
        } else {
          // Remaining images: force lazy loading
          img.loading = 'lazy';
          img.removeAttribute('fetchpriority');
          img.decoding = 'async';
          
          // Mobile: optimize all images
          if (isMobile) {
            optimizeSrcset(img);
          }
        }
      });
    }

    // Optimize thumbnail images
    if (thumbsWrapper) {
      const thumbs = thumbsWrapper.querySelectorAll('.swiper-slide img');
      thumbs.forEach((img, index) => {
        if (index < config.thumbEagerCount) {
          img.loading = 'eager';
        } else {
          img.loading = 'lazy';
        }
        
        // Thumbnails: always use small size
        if (img.srcset) {
          const smallEntry = img.srcset.split(',')[0];
          if (smallEntry) {
            img.src = smallEntry.trim().split(' ')[0];
          }
        }
      });
    }

    // Optimize grid layout (desktop only)
    if (gridWrapper && !isMobile) {
      const gridItems = gridWrapper.querySelectorAll('.main-product__media--grid-item');
      gridItems.forEach((item, index) => {
        const img = item.querySelector('img');
        if (!img) return;

        if (index < 2) {
          img.loading = 'eager';
          if (index === 0) img.fetchPriority = 'high';
        } else {
          img.loading = 'lazy';
        }
      });
    }
  }

  /**
   * Video lazy loading using Intersection Observer
   */
  function optimizeVideoLoading() {
    const videos = document.querySelectorAll('.main-product__media video');
    
    if (!videos.length) return;

    const videoObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const video = entry.target;
        if (entry.isIntersecting) {
          if (video.paused) {
            video.play().catch(() => {});
          }
        } else {
          if (!video.paused) {
            video.pause();
          }
        }
      });
    }, { rootMargin: config.rootMargin });

    videos.forEach(video => {
      video.pause();
      video.preload = isMobile ? 'none' : 'metadata';
      videoObserver.observe(video);
    });

    log('Video optimization applied to', videos.length, 'videos');
  }

  /**
   * Improve Swiper initialization timing
   * Thumbs must initialize before gallery for proper linking
   */
  function patchSwiperInitTiming() {
    const originalDefine = customElements.define.bind(customElements);
    
    // Track initialization order
    window.swiperInitQueue = window.swiperInitQueue || {
      thumbsReady: false,
      galleryPending: null
    };
    
    customElements.define = function(name, constructor, options) {
      if (name === 'swiper-product-thumbs') {
        const originalConnectedCallback = constructor.prototype.connectedCallback;
        
        constructor.prototype.connectedCallback = function() {
          if (Shopify.designMode) {
            originalConnectedCallback.call(this);
            return;
          }
          
          // Thumbs init immediately
          const firstImage = this.querySelector('.swiper-slide:first-child img');
          
          const doInit = () => {
            this.init();
            window.swiperInitQueue.thumbsReady = true;
            log('Thumbs initialized');
            
            // If gallery was waiting, init it now
            if (window.swiperInitQueue.galleryPending) {
              log('Gallery was pending, init now');
              window.swiperInitQueue.galleryPending();
              window.swiperInitQueue.galleryPending = null;
            }
          };
          
          if (firstImage && firstImage.complete) {
            requestAnimationFrame(doInit);
          } else if (firstImage) {
            firstImage.addEventListener('load', () => requestAnimationFrame(doInit), { once: true });
            setTimeout(() => { if (!this.swiperInitialized) doInit(); }, 1500);
          } else {
            doInit();
          }
        };
        
        log('Patched:', name);
      }
      
      if (name === 'swiper-product-gallery') {
        const originalConnectedCallback = constructor.prototype.connectedCallback;
        
        constructor.prototype.connectedCallback = function() {
          if (Shopify.designMode) {
            originalConnectedCallback.call(this);
            return;
          }
          
          const self = this;
          const firstImage = this.querySelector('.swiper-slide:first-child img');
          
          const doInit = () => {
            // Wait for thumbs if gallery has thumbs attribute
            if (self.hasAttribute('thumbs') && !window.swiperInitQueue.thumbsReady) {
              log('Gallery waiting for thumbs...');
              window.swiperInitQueue.galleryPending = () => {
                self.init();
                log('Gallery initialized after thumbs');
              };
              // Fallback timeout
              setTimeout(() => {
                if (!self.swiperInitialized) {
                  log('Gallery timeout fallback');
                  self.init();
                }
              }, 2000);
            } else {
              self.init();
              log('Gallery initialized');
            }
          };
          
          if (firstImage && firstImage.complete) {
            requestAnimationFrame(doInit);
          } else if (firstImage) {
            firstImage.addEventListener('load', () => requestAnimationFrame(doInit), { once: true });
            const timeout = isMobile ? 2000 : 3000;
            setTimeout(() => { if (!self.swiperInitialized) doInit(); }, timeout);
          } else {
            doInit();
          }
        };
        
        log('Patched:', name);
      }

      return originalDefine(name, constructor, options);
    };
  }

  /**
   * Add preload hints for critical resources
   */
  function addPreloadHints() {
    const firstSlide = document.querySelector('.main-product__media--slider .swiper-slide:first-child img');
    
    if (firstSlide && firstSlide.srcset) {
      const srcsetEntries = firstSlide.srcset.split(',').map(s => s.trim());
      
      // Select appropriate size based on device
      const targetSize = isMobile ? '480w' : '800w';
      const targetEntry = srcsetEntries.find(s => s.includes(targetSize)) 
                         || srcsetEntries.find(s => s.includes('600w'))
                         || srcsetEntries[0];
      const preloadUrl = targetEntry?.split(' ')[0];

      if (preloadUrl && !document.querySelector(`link[href="${preloadUrl}"]`)) {
        const preload = document.createElement('link');
        preload.rel = 'preload';
        preload.as = 'image';
        preload.href = preloadUrl;
        preload.fetchPriority = 'high';
        document.head.appendChild(preload);
        log('Added preload hint:', preloadUrl.substring(0, 50));
      }
    }
  }

  /**
   * Disable unnecessary features on mobile for faster rendering
   */
  function optimizeMobileRendering() {
    if (!isMobile) return;

    // Disable hover effects and animations during initial load
    const style = document.createElement('style');
    style.id = 'mobile-perf-boost';
    style.textContent = `
      .main-product__media * {
        animation-delay: 0s !important;
        transition-delay: 0s !important;
      }
      .main-product__media img:not([loading="eager"]) {
        content-visibility: auto;
      }
    `;
    document.head.appendChild(style);

    // Remove performance styles after load
    window.addEventListener('load', () => {
      setTimeout(() => {
        const perfStyle = document.getElementById('mobile-perf-boost');
        if (perfStyle) perfStyle.remove();
      }, 1000);
    });

    log('Mobile rendering optimizations applied');
  }

  /**
   * Connection-aware loading
   */
  function applyConnectionAwareLoading() {
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    
    if (connection) {
      const slowConnection = connection.saveData || 
                            connection.effectiveType === 'slow-2g' || 
                            connection.effectiveType === '2g';
      
      if (slowConnection) {
        config.mobileMaxWidth = 400;
        config.thumbEagerCount = 2;
        log('Slow connection detected, reducing image sizes');
      }
    }
  }

  /**
   * Initialize optimizer
   */
  function init() {
    applyConnectionAwareLoading();
    patchSwiperInitTiming();

    if (document.readyState === 'loading') {
      // Run ASAP for mobile
      if (isMobile) {
        optimizeMobileRendering();
      }
      
      document.addEventListener('DOMContentLoaded', () => {
        optimizeImageLoading();
        optimizeVideoLoading();
        addPreloadHints();
      });
    } else {
      if (isMobile) {
        optimizeMobileRendering();
      }
      optimizeImageLoading();
      optimizeVideoLoading();
      addPreloadHints();
    }

    log('Optimizer initialized | Mobile:', isMobile);
  }

  init();

})();